import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Users,
  MessageCircle,
  Menu,
  X,
  Sparkles,
  ListChecks,
  DollarSign,
  Search,
  ShoppingBag,
  Store,
  Gavel,
  Ticket,
  Home,
  Truck,
} from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_merchant-scores-1/artifacts/kmv9mriv_v3bk3q6t_file_00000000133c71fda8bb4b05261095cf.png";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === "/";

  useEffect(() => {
    if (!user || (user.role !== "customer" && user.role !== "business")) {
      setUnread(0);
      return;
    }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/threads/unread-count");
        if (!cancelled) setUnread(data?.unread_total || 0);
      } catch {
        /* ignore transient errors */
      }
    };
    fetchUnread();
    const h = setInterval(fetchUnread, 20000);
    return () => {
      cancelled = true;
      clearInterval(h);
    };
  }, [user]);

  // Anchor links only make sense on the landing page — otherwise route home first.
  const anchor = (id) => (isHome ? `#${id}` : `/#${id}`);

  // Second-line menu items. Parent items without an `href` are pure
  // dropdown labels (no navigation on click) — they just reveal their
  // submenu on hover/focus.
  const menuItems = [
    {
      label: "Why Servrevu",
      icon: Sparkles,
      // No href — parent is a pure dropdown label (does not navigate)
      testId: "menu-why",
      submenu: [
        { label: "How it works", icon: ListChecks, href: anchor("how-it-works"), external: true, testId: "menu-how" },
        { label: "Features",     icon: Sparkles,   href: anchor("features"),     external: true, testId: "menu-features" },
        { label: "Pricing",      icon: DollarSign, href: anchor("pricing"),      external: true, testId: "menu-pricing" },
      ],
    },
    { label: "Directory",    icon: Search,     href: "/directory",        testId: "menu-directory" },
    {
      label: "Shopping",
      icon: ShoppingBag,
      // No href — dropdown-only parent
      testId: "menu-shopping",
      submenu: [
        { label: "Shops",       icon: Store,       href: "/shops",       testId: "menu-shops" },
        { label: "Marketplace", icon: ShoppingBag, href: "/marketplace", testId: "menu-marketplace" },
        { label: "Auctions",    icon: Gavel,       href: "/auctions",    testId: "menu-auctions" },
      ],
    },
    { label: "Food Trucks",  icon: Truck,      href: "/foodtrucks",       testId: "menu-foodtrucks" },
    { label: "Events",       icon: Ticket,     href: "/events",           testId: "menu-events" },
    { label: "Neighborhood", icon: Home,      href: "/find-neighborhood",  testId: "menu-neighborhood" },
  ];

  const isActive = (href) => {
    if (href.startsWith("#") || href.startsWith("/#")) return false;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  return (
    <header className="sticky top-0 z-40 bg-[#FDFBF9]/95 backdrop-blur-md border-b border-[#EAE3DE]">
      {/* Row 1 — logo + auth controls */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
          <img
            src={LOGO_URL}
            alt="Servrevu"
            className="h-16 md:h-20 w-auto object-contain"
          />
        </Link>

        <div className="flex items-center gap-2">
          {(user?.role === "customer" || user?.role === "business") && (
            <Link
              to="/messages"
              className="hidden md:inline-flex items-center gap-1.5 text-[14px] text-[#3E2A26] hover:text-[#B31E1E] transition-colors relative mr-3"
              data-testid="nav-messages"
            >
              <MessageCircle size={14} /> Messages
              {unread > 0 && (
                <span
                  className="ml-0.5 min-w-[18px] h-[18px] rounded-full bg-[#B31E1E] text-white text-[10px] font-mono flex items-center justify-center px-1"
                  data-testid="nav-messages-unread"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </Link>
          )}

          {user ? (
            <>
              <NotificationsBell />
              <Badge variant="outline" className="rounded-full font-mono text-[10px] border-[#EAE3DE] hidden sm:inline-flex">
                {user.role === "business" ? <Building2 size={12} className="mr-1" /> : <Users size={12} className="mr-1" />}
                {user.role}
              </Badge>
              <span className="hidden md:inline text-sm text-[#5C4E48]" data-testid="nav-user-name">{user.name}</span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg border-[#EAE3DE] bg-white hover:bg-[#FAF5F2] text-[#1B1614]"
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="nav-logout-button"
              >
                Log out
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden md:inline text-[14px] text-[#3E2A26] hover:text-[#B31E1E] mr-2" data-testid="nav-login">
                Log in
              </Link>
              <Link to="/register">
                <Button size="sm" className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white h-9 px-4 font-semibold" data-testid="nav-signup-button">
                  Get Started
                </Button>
              </Link>
            </>
          )}
          <button
            className="md:hidden p-2 ml-1 rounded-md hover:bg-[#FAF5F2]"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Row 2 — second-line horizontal menu (desktop only) */}
      <nav
        className="hidden md:block border-t border-[#EAE3DE] bg-white"
        data-testid="nav-submenu-row"
        aria-label="Primary"
      >
        <ul className="max-w-6xl mx-auto px-6 md:px-10 flex items-center gap-1 h-11 overflow-visible">
          {menuItems.map((it) => {
            const active = it.href ? isActive(it.href) : false;
            const cls = `inline-flex items-center gap-2 px-3 h-full text-[13.5px] font-medium border-b-2 transition-colors whitespace-nowrap ${
              active
                ? "text-[#B31E1E] border-[#B31E1E]"
                : "text-[#3E2A26] border-transparent hover:text-[#B31E1E] hover:border-[#F0C9C4]"
            }`;

            // Dropdown parent — reveals its submenu on hover/focus. Parents
            // with an `href` also navigate on click; parents without one are
            // pure labels (rendered as a <button> so keyboard focus still
            // reveals the menu without any navigation).
            if (it.submenu) {
              const chevron = (
                <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden className="ml-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              );
              return (
                <li key={it.label} className="relative group" data-testid={`${it.testId}-parent`}>
                  {it.href ? (
                    <a href={it.href} className={cls} data-testid={it.testId}>
                      <it.icon size={14} className="text-[#B31E1E]" />
                      {it.label}
                      {chevron}
                    </a>
                  ) : (
                    <button
                      type="button"
                      className={`${cls} cursor-default`}
                      data-testid={it.testId}
                      aria-haspopup="true"
                      onClick={(e) => e.preventDefault()}
                    >
                      <it.icon size={14} className="text-[#B31E1E]" />
                      {it.label}
                      {chevron}
                    </button>
                  )}
                  <div
                    className="absolute left-0 top-full pt-1 min-w-[200px] invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-150 z-50"
                    data-testid={`${it.testId}-submenu`}
                  >
                    <ul className="bg-white border border-[#EAE3DE] rounded-md shadow-lg overflow-hidden py-1">
                      {it.submenu.map((sub) => (
                        <li key={sub.label}>
                          {sub.external ? (
                            <a
                              href={sub.href}
                              className="flex items-center gap-2 px-3 py-2 text-[13.5px] text-[#3E2A26] hover:bg-[#FAF5F2] hover:text-[#B31E1E]"
                              data-testid={sub.testId}
                            >
                              <sub.icon size={13} className="text-[#B31E1E]" />
                              {sub.label}
                            </a>
                          ) : (
                            <Link
                              to={sub.href}
                              className="flex items-center gap-2 px-3 py-2 text-[13.5px] text-[#3E2A26] hover:bg-[#FAF5F2] hover:text-[#B31E1E]"
                              data-testid={sub.testId}
                            >
                              <sub.icon size={13} className="text-[#B31E1E]" />
                              {sub.label}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              );
            }

            return (
              <li key={it.label}>
                {it.external ? (
                  <a href={it.href} className={cls} data-testid={it.testId}>
                    <it.icon size={14} className="text-[#B31E1E]" />
                    {it.label}
                  </a>
                ) : (
                  <Link to={it.href} className={cls} data-testid={it.testId}>
                    <it.icon size={14} className="text-[#B31E1E]" />
                    {it.label}
                  </Link>
                )}
              </li>
            );
          })}
          {user?.role === "business" && (
            <li className="ml-auto">
              <Link
                to="/dashboard/business"
                className="inline-flex items-center gap-2 px-3 h-full text-[13.5px] font-medium border-b-2 border-transparent text-[#3E2A26] hover:text-[#B31E1E] hover:border-[#F0C9C4] whitespace-nowrap"
                data-testid="menu-business-dashboard"
              >
                <Building2 size={14} className="text-[#B31E1E]" /> Console
              </Link>
            </li>
          )}
          {user?.role === "customer" && (
            <li className="ml-auto">
              <Link
                to="/dashboard/customer"
                className="inline-flex items-center gap-2 px-3 h-full text-[13.5px] font-medium border-b-2 border-transparent text-[#3E2A26] hover:text-[#B31E1E] hover:border-[#F0C9C4] whitespace-nowrap"
                data-testid="menu-customer-dashboard"
              >
                <Users size={14} className="text-[#B31E1E]" /> Profile
              </Link>
            </li>
          )}
          {user?.role === "admin" && (
            <li className="ml-auto">
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 px-3 h-full text-[13.5px] font-semibold border-b-2 border-transparent text-[#B31E1E] hover:border-[#B31E1E] whitespace-nowrap"
                data-testid="menu-admin"
              >
                <ListChecks size={14} /> Admin
              </Link>
            </li>
          )}
        </ul>
      </nav>

      {mobileOpen && (
        <div className="md:hidden border-t border-[#EAE3DE] bg-white" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1 text-[14px] text-[#3E2A26]">
            <div className="text-[10px] uppercase tracking-wider text-[#8B7B73] pt-1 pb-1">Explore</div>
            {menuItems.map((it) => (
              <div key={it.label}>
                {!it.href ? (
                  // Pure-label parent — render as a non-nav heading; the
                  // submenu items below it still navigate.
                  <div
                    className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-[#3E2A26] font-medium"
                    data-testid={`mobile-${it.testId}`}
                  >
                    <it.icon size={15} className="text-[#B31E1E]" />
                    {it.label}
                  </div>
                ) : it.external ? (
                  <a
                    href={it.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#FAF5F2]"
                    data-testid={`mobile-${it.testId}`}
                  >
                    <it.icon size={15} className="text-[#B31E1E]" />
                    {it.label}
                  </a>
                ) : (
                  <Link
                    to={it.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#FAF5F2]"
                    data-testid={`mobile-${it.testId}`}
                  >
                    <it.icon size={15} className="text-[#B31E1E]" />
                    {it.label}
                  </Link>
                )}
                {it.submenu?.map((sub) => (
                  sub.external ? (
                    <a
                      key={sub.label}
                      href={sub.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2.5 rounded-lg pl-8 pr-2 py-2 hover:bg-[#FAF5F2] text-[13px] text-[#5C4E48]"
                      data-testid={`mobile-${sub.testId}`}
                    >
                      <sub.icon size={13} className="text-[#B31E1E]" />
                      {sub.label}
                    </a>
                  ) : (
                    <Link
                      key={sub.label}
                      to={sub.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2.5 rounded-lg pl-8 pr-2 py-2 hover:bg-[#FAF5F2] text-[13px] text-[#5C4E48]"
                      data-testid={`mobile-${sub.testId}`}
                    >
                      <sub.icon size={13} className="text-[#B31E1E]" />
                      {sub.label}
                    </Link>
                  )
                ))}
              </div>
            ))}
            {(user?.role === "customer" || user?.role === "business") && (
              <Link
                to="/messages"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#FAF5F2]"
                data-testid="mobile-menu-messages"
              >
                <MessageCircle size={15} className="text-[#B31E1E]" />
                Messages {unread > 0 && `(${unread})`}
              </Link>
            )}
            {user && (
              <>
                <div className="text-[10px] uppercase tracking-wider text-[#8B7B73] pt-3 pb-1">Your account</div>
                {user.role === "business" && (
                  <Link to="/dashboard/business" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#FAF5F2]">
                    <Building2 size={15} className="text-[#B31E1E]" /> Business Console
                  </Link>
                )}
                {user.role === "customer" && (
                  <Link to="/dashboard/customer" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#FAF5F2]">
                    <Users size={15} className="text-[#B31E1E]" /> My Profile
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#FAF5F2] font-medium">
                    <ListChecks size={15} className="text-[#B31E1E]" /> Admin Panel
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
